import Box from "@mui/material/Box";
import Header from "../components/header/header";
import Footer from "../components/footer/footer";
import { ThemeProvider } from "@mui/material/styles";
import Typography from "@mui/material/Typography";
import Snackbar from "@mui/material/Snackbar";

import { textStyle } from "src/styles/textstyle";
import TextField from "@mui/material/TextField";
import Autocomplete from "@mui/material/Autocomplete";
import MainButton from "src/components/buttons/mainbutton";
import SecondaryButton from "src/components/buttons/secondbutton";
import { InnerContentBox } from "src/components/ContentBox/ContentBox";
import { TextBoxWithTitle } from "./UserPage/OwnUserPage";
import ApartmentIcon from "@mui/icons-material/Apartment";
import HomeIcon from "@mui/icons-material/Home";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import { useCallback, useEffect, useRef, useState } from "react";
import {
  useCreateHomeMutation,
  useGetOwnHomeQuery,
  useUpdateHomeMutation,
} from "src/redux/service/home.service";
import CircularProgress from "@mui/material/CircularProgress";
import { useSnackbar } from "notistack";
import ButtonBase from "@mui/material/ButtonBase";
import theme, { backgroundColor, mainColor } from "src/styles/mainstyle";
import Grid from "@mui/material/Grid";
import IncrementorButton from "src/components/buttons/incrementorbutton";
import { Home } from "src/model_types/home.types";
import AvailabilitySelector from "src/components/availability/availability";
import { useUploadPhotoMutation } from "src/redux/service/photo.service";
import AvatarDropzone from "src/components/photo/photo.dropzone";
import ImagesModal from "src/components/modals/ImagesModal";
import { Tooltip, useMediaQuery, useTheme } from "@mui/material";
import { useLocation } from "react-router-dom";
import { trackPostHogEvent } from "src/analytics/posthog";

type AddressSuggestion = {
  fullText: string;
  placeId: string;
  primaryText: string;
  secondaryText?: string;
};

const getAddressComponent = (
  components:
    | Array<{ long_name?: string; longText?: string; types?: string[] }>
    | undefined,
  type: string,
): string | undefined => {
  const match = components?.find((component) =>
    component.types?.includes(type),
  );
  return match?.longText || match?.long_name;
};

export default function HomePage() {
  const location = useLocation();
  const isOnboardingEmbed =
    new URLSearchParams(location.search).get("onboarding") === "1";
  const googleMapsApiKey =
    ((window as any).env?.REACT_APP_GOOGLE_MAPS_API_KEY as string | undefined) ||
    "";

  const {
    data: home,
    isLoading: isHomeLoading,
    refetch,
  } = useGetOwnHomeQuery();
  const [updateHome] = useUpdateHomeMutation();
  const [createHome] = useCreateHomeMutation();
  const [isUpdatingOrCreating, setIsUpdatingOrCreating] = useState(false);

  const [uploadPhoto] = useUploadPhotoMutation();
  const mobileTheme = useTheme();
  const isDesktop = useMediaQuery(mobileTheme.breakpoints.down("sm"));

  const [open, setOpen] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [hasAttemptedSave, setHasAttemptedSave] = useState(false);
  const [addressSuggestions, setAddressSuggestions] = useState<AddressSuggestion[]>(
    [],
  );
  const [isAddressLoading, setIsAddressLoading] = useState(false);
  const hasTrackedPageOpenRef = useRef(false);

  const handleOpen = (index: number) => {
    setCurrentImageIndex(index);
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const handlePrev = () => {
    setCurrentImageIndex((prevIndex) =>
      prevIndex === 0 ? (home?.files!.length || 0) - 1 : prevIndex - 1,
    );
  };

  const [showEnglishHint, setShowEnglishHint] = useState(false);

  const handleNext = () => {
    setCurrentImageIndex((prevIndex) =>
      prevIndex === (home?.files!.length || 0) - 1 ? 0 : prevIndex + 1,
    );
  };

  const onDrop = useCallback(
    (acceptedFiles: File[]) => {
      const file = acceptedFiles[0];
      if (!home?._id) {
        console.error("Home ID not found");
      }

      const metaData = {
        homeId: String(home?._id),
      };

      // Attempt to upload the file
      uploadPhoto({ file, metaData });
    },
    [uploadPhoto, home, refetch],
  );

  const [updateData, setUpdateData] = useState<Partial<Home>>({
    isActive: true,
    houseType: "apartment",
    availability: {
      type: "anytime",
    },
  });
  const { enqueueSnackbar } = useSnackbar();
  const trimmedTitle = updateData.descriptions?.title?.trim() ?? "";
  const isTitleMissing = trimmedTitle.length === 0;

  useEffect(() => {
    if (home) {
      // Deep clone to avoid circular references from RTK Query
      const cleanHome = JSON.parse(JSON.stringify(home));
      setUpdateData(cleanHome);
    }
  }, [home]);

  useEffect(() => {
    const query = (updateData.address || "").trim();
    if (!googleMapsApiKey || query.length < 3) {
      setAddressSuggestions([]);
      setIsAddressLoading(false);
      return;
    }

    const controller = new AbortController();
    const timeoutId = window.setTimeout(async () => {
      setIsAddressLoading(true);
      try {
        const response = await fetch(
          "https://places.googleapis.com/v1/places:autocomplete",
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              "X-Goog-Api-Key": googleMapsApiKey,
              "X-Goog-FieldMask":
                "suggestions.placePrediction.placeId,suggestions.placePrediction.text,suggestions.placePrediction.structuredFormat",
            },
            body: JSON.stringify({
              input: query,
              includedRegionCodes: ["se"],
            }),
            signal: controller.signal,
          },
        );

        if (!response.ok) {
          throw new Error(`Autocomplete request failed (${response.status})`);
        }

        const data = await response.json();
        const suggestions = (data?.suggestions || [])
          .map((item: any) => item?.placePrediction)
          .filter(Boolean)
          .map((prediction: any): AddressSuggestion => ({
            placeId: prediction.placeId,
            fullText: prediction.text?.text || "",
            primaryText: prediction.structuredFormat?.mainText?.text || "",
            secondaryText: prediction.structuredFormat?.secondaryText?.text,
          }))
          .filter((item: AddressSuggestion) => Boolean(item.placeId));

        setAddressSuggestions(suggestions);
      } catch (error) {
        if (!controller.signal.aborted) {
          console.error("Failed to fetch address suggestions", error);
          setAddressSuggestions([]);
        }
      } finally {
        if (!controller.signal.aborted) {
          setIsAddressLoading(false);
        }
      }
    }, 250);

    return () => {
      controller.abort();
      window.clearTimeout(timeoutId);
    };
  }, [googleMapsApiKey, updateData.address]);

  const handleAddressSelection = async (selection: AddressSuggestion) => {
    if (!googleMapsApiKey || !selection.placeId) {
      return;
    }

    try {
      const response = await fetch(
        `https://places.googleapis.com/v1/places/${selection.placeId}`,
        {
          headers: {
            "X-Goog-Api-Key": googleMapsApiKey,
            "X-Goog-FieldMask":
              "formattedAddress,location,addressComponents",
          },
        },
      );

      if (!response.ok) {
        throw new Error(`Place details request failed (${response.status})`);
      }

      const place = await response.json();
      const lat = place?.location?.latitude;
      const lng = place?.location?.longitude;

      setUpdateData((prev) => ({
        ...prev,
        address: place?.formattedAddress || selection.fullText || prev.address,
        city:
          getAddressComponent(place?.addressComponents, "locality") ||
          getAddressComponent(place?.addressComponents, "postal_town") ||
          prev.city,
        country:
          getAddressComponent(place?.addressComponents, "country") || prev.country,
        zipCode:
          getAddressComponent(place?.addressComponents, "postal_code") || prev.zipCode,
        location:
          typeof lat === "number" && typeof lng === "number"
            ? { lat, lng }
            : prev.location,
      }));
    } catch (error) {
      console.error("Failed to fetch selected place details", error);
      setUpdateData((prev) => ({
        ...prev,
        address: selection.fullText || prev.address,
      }));
    }
  };

  useEffect(() => {
    if (isHomeLoading || hasTrackedPageOpenRef.current) {
      return;
    }

    trackPostHogEvent("my_home_page_opened", {
      has_home: Boolean(home?._id),
      is_onboarding_embed: isOnboardingEmbed,
      source: isOnboardingEmbed ? "onboarding" : "my_home_page",
    });

    hasTrackedPageOpenRef.current = true;
  }, [isHomeLoading, home?._id, isOnboardingEmbed, hasTrackedPageOpenRef]);

  const handleUpdate = async (overrides?: Partial<Home>) => {
    setHasAttemptedSave(true);

    const mergedData: Partial<Home> = {
      ...(updateData || {}),
      ...(overrides || {}),
    };

    const title = mergedData.descriptions?.title?.trim();
    if (!title) {
      enqueueSnackbar("Title for your home is required.", {
        variant: "error",
      });
      return false;
    }

    const normalizedData: Partial<Home> = {
      ...mergedData,
      descriptions: {
        ...mergedData.descriptions,
        title,
      },
    };

    setIsUpdatingOrCreating(true);
    try {
      const {
        ownerId,
        likedByUserIds,
        allowedUserIds,
        files,
        requestedUserIds,
        ...payload
      } = normalizedData;

      // If you want optimistic UI, you can setUpdateData(mergedData) here.
      // setUpdateData(mergedData);

      let operation: "update" | "create" | "updateExisting" = "update";

      if (home?._id) {
        await updateHome({ ...payload, _id: home._id }).unwrap();
      } else {
        // Guard against stale client state: re-check server before creating.
        const latestHomeResult = await refetch();
        const existingHomeId = latestHomeResult.data?._id;

        if (existingHomeId) {
          await updateHome({ ...payload, _id: existingHomeId }).unwrap();
          operation = "updateExisting";
        } else {
          await createHome(payload).unwrap();
          operation = "create";
        }
      }

      // Update local state to reflect the successful change
      setUpdateData(normalizedData);

      if (operation === "create") {
        trackPostHogEvent("home_create_succeeded", {
          has_home: true,
          is_onboarding_embed: isOnboardingEmbed,
          source: isOnboardingEmbed ? "onboarding" : "my_home_page",
        });
        enqueueSnackbar("Create successful!", { variant: "success" });
      } else if (operation === "updateExisting") {
        trackPostHogEvent("home_update_succeeded", {
          has_home: true,
          is_onboarding_embed: isOnboardingEmbed,
          source: "create_guard",
        });
        enqueueSnackbar("You already had a home. We updated it instead.", {
          variant: "info",
        });
      } else {
        trackPostHogEvent("home_update_succeeded", {
          has_home: true,
          is_onboarding_embed: isOnboardingEmbed,
          source: isOnboardingEmbed ? "onboarding" : "my_home_page",
        });
        enqueueSnackbar("Update successful!", { variant: "success" });
      }

      setHasAttemptedSave(false);

      return true;
    } catch (error) {
      console.log("Error:", error);
      const conflictError =
        (error as any)?.status === 409 ||
        (error as any)?.response?.status === 409;
      enqueueSnackbar(
        conflictError
          ? "You can only have one home per account. Existing home detected."
          : "Update failed. Please try again.",
        { variant: "error" },
      );
      return false;
    } finally {
      setIsUpdatingOrCreating(false);
    }
  };

  useEffect(() => {
    if (!isOnboardingEmbed) {
      return;
    }

    const handleMessage = async (event: MessageEvent) => {
      const data = event.data;
      if (
        !data ||
        data.type !== "homeswap:onboarding-save-request" ||
        data.page !== "home"
      ) {
        return;
      }

      const success = await handleUpdate();
      window.parent.postMessage(
        {
          type: "homeswap:onboarding-save-result",
          page: "home",
          requestId: data.requestId,
          success,
        },
        window.location.origin,
      );
    };

    window.addEventListener("message", handleMessage);
    return () => window.removeEventListener("message", handleMessage);
  }, [isOnboardingEmbed, updateData]);

  if (isHomeLoading) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
        }}
      >
        <CircularProgress />
      </div>
    );
  }
  const boxStyle = {
    ...(!isDesktop && { maxWidth: "33.33vw" }),
    ...(!isDesktop && { height: "100%" }),
    flexGrow: 1,
    overflow: "hidden",
  };

  const pagePadding = isOnboardingEmbed
    ? { xs: 1, sm: 1.5, md: 2 }
    : { xs: 2, sm: 3, md: 5 };

  const gridSpacing = isOnboardingEmbed
    ? { xs: 2, sm: 2, md: 2, lg: 2 }
    : { xs: 3, sm: 4, md: 6, lg: 8 };

  return (
    <Box>
      <ThemeProvider theme={theme}>
        {!isOnboardingEmbed && <Header />}
        <Box
          sx={{
            p: pagePadding,
            mt: isOnboardingEmbed ? 0 : 10,
          }}
        >
          <Box sx={{ pt: isOnboardingEmbed ? 0 : 10 }}>
            <Grid
              container
              spacing={gridSpacing}
              justifyContent="space-between"
              alignItems="stretch"
            >
              <Grid
                item
                xs={12}
                sm={4}
                sx={{ display: "flex", flexDirection: "column" }}
              >
                <InnerContentBox title={""} sx={{ ...boxStyle, overflow: "visible" }}>
                  <Box>
                    <Typography
                      variant="h5"
                      component="h5"
                      sx={{ textStyle, fontSize: "1.0rem" }}
                    >
                      My home
                    </Typography>
                    <Box display="flex" justifyContent="center">
                      <ButtonBase
                        onClick={() =>
                          setUpdateData({
                            ...updateData,
                            houseType: "apartment",
                          })
                        }
                      >
                        <Box
                          display="flex"
                          flexDirection="column"
                          alignItems="center"
                        >
                          <ApartmentIcon
                            style={{
                              fontSize: 60,
                              color:
                                updateData.houseType === "apartment"
                                  ? "black"
                                  : mainColor,
                            }}
                          />
                          <Typography
                            variant="caption"
                            component="p"
                            sx={textStyle}
                          >
                            Apartment
                          </Typography>
                        </Box>
                      </ButtonBase>

                      <Box width={50} />

                      <ButtonBase
                        onClick={() =>
                          setUpdateData({ ...updateData, houseType: "house" })
                        }
                      >
                        <Box
                          display="flex"
                          flexDirection="column"
                          alignItems="center"
                        >
                          <HomeIcon
                            style={{
                              fontSize: 60,
                              color:
                                updateData.houseType === "house"
                                  ? "black"
                                  : mainColor,
                            }}
                          />
                          <Typography
                            variant="caption"
                            component="p"
                            sx={textStyle}
                          >
                            House
                          </Typography>
                        </Box>
                      </ButtonBase>
                    </Box>
                    <Box sx={{ mt: 2, position: "relative", zIndex: 20 }}>
                      <Autocomplete
                        fullWidth
                        freeSolo
                        value={updateData.address || ""}
                        options={addressSuggestions}
                        loading={isAddressLoading}
                        forcePopupIcon={false}
                        isOptionEqualToValue={(option, value) =>
                          typeof value !== "string" &&
                          option.placeId === value.placeId
                        }
                        filterOptions={(options) => options}
                        getOptionLabel={(option) =>
                          typeof option === "string" ? option : option.fullText
                        }
                        inputValue={updateData.address || ""}
                        onInputChange={(_, newValue, reason) => {
                          if (reason === "input" || reason === "clear") {
                            setUpdateData((prev) => ({
                              ...prev,
                              address: newValue,
                              location: undefined,
                            }));
                          }
                        }}
                        onChange={(_, selection) => {
                          if (selection && typeof selection !== "string") {
                            void handleAddressSelection(selection);
                            return;
                          }

                          if (typeof selection === "string") {
                            setUpdateData((prev) => ({
                              ...prev,
                              address: selection,
                              location: undefined,
                            }));
                          }
                        }}
                        renderOption={(props, option) => (
                          <li {...props}>
                            <Box>
                              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                {option.primaryText || option.fullText}
                              </Typography>
                              {option.secondaryText && (
                                <Typography variant="caption" color="text.secondary">
                                  {option.secondaryText}
                                </Typography>
                              )}
                            </Box>
                          </li>
                        )}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label="Address"
                            margin="none"
                            sx={{ backgroundColor: theme.palette.primary.main }}
                            placeholder={"Start typing and pick an address from suggestions"}
                            InputProps={{
                              ...params.InputProps,
                              endAdornment: (
                                <>
                                  {isAddressLoading ? (
                                    <CircularProgress color="inherit" size={18} />
                                  ) : null}
                                  {params.InputProps.endAdornment}
                                </>
                              ),
                            }}
                          />
                        )}
                      />
                    </Box>
                    <Box display="flex" justifyContent="center" pb={2} pt={1}>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ width: "100%", textAlign: "center" }}
                      >
                        {"Select an address suggestion to auto-fill map location."}
                      </Typography>
                    </Box>
                    <Box
                      display="flex"
                      justifyContent="center"
                      alignItems="center"
                      pt={2}
                      paddingBottom={4}
                    >
                      <TextField
                        label="Surface area"
                        sx={{
                          backgroundColor: theme.palette.primary.main,
                          width: "60%",
                        }}
                        onChange={(e) =>
                          setUpdateData({
                            ...updateData,
                            surfaceArea: {
                              unit: "m2",
                              ...updateData.surfaceArea,
                              number: Number(e.target.value),
                            },
                          })
                        }
                        InputLabelProps={{
                          shrink: true,
                        }}
                        value={updateData.surfaceArea?.number || 0}
                      />
                      <Box width={20} />
                      <Select
                        key={updateData.surfaceArea?.unit}
                        value={updateData.surfaceArea?.unit || "m2"}
                        sx={{
                          backgroundColor: theme.palette.primary.main,
                          width: "35%",
                        }}
                        onChange={(e) =>
                          setUpdateData({
                            ...updateData,
                            surfaceArea: {
                              number: 0,
                              ...updateData.surfaceArea,
                              unit: e.target.value as "m2" | "ft2",
                            },
                          })
                        }
                      >
                        <MenuItem value={"ft2"}>sq ft</MenuItem>
                        <MenuItem value={"m2"}>m^2</MenuItem>
                      </Select>
                    </Box>
                    <IncrementorButton
                      header={"Bedrooms"}
                      value={updateData?.extras?.bedrooms || 0}
                      onUpdate={(newValue) =>
                        setUpdateData({
                          ...updateData,
                          extras: {
                            ...updateData.extras,
                            bedrooms: newValue,
                          },
                        })
                      }
                    />
                    <IncrementorButton
                      header={"Bathrooms"}
                      value={updateData?.extras?.bathrooms || 0}
                      onUpdate={(newValue) =>
                        setUpdateData({
                          ...updateData,
                          extras: {
                            ...updateData.extras,
                            bathrooms: newValue,
                          },
                        })
                      }
                    />
                    <IncrementorButton
                      header={"Kitchens"}
                      value={updateData?.extras?.kitchens || 0}
                      onUpdate={(newValue) =>
                        setUpdateData({
                          ...updateData,
                          extras: {
                            ...updateData.extras,
                            kitchens: newValue,
                          },
                        })
                      }
                    />
                    <Box width={20} />
                  </Box>
                </InnerContentBox>
              </Grid>
              <Grid
                item
                xs={12}
                sm={4}
                sx={{ display: "flex", flexDirection: "column" }}
              >
                <InnerContentBox title={""} sx={boxStyle}>
                  <Box>
                    <Typography
                      variant="h5"
                      component="h5"
                      sx={{
                        ...textStyle,
                        // overflow: "hidden", // Ensure text doesn't escape
                        // textOverflow: "ellipsis", // Add ellipsis if text is too long
                        // whiteSpace: "nowrap", // Prevent the text from wrapping if needed
                      }}
                    >
                      Describe your home
                    </Typography>
                    <TextBoxWithTitle
                      nRows={2}
                      title={"Title for your home"}
                      required
                      error={hasAttemptedSave && isTitleMissing}
                      helperText={
                        hasAttemptedSave && isTitleMissing
                          ? "Title is required"
                          : undefined
                      }
                      onChange={(e) => {
                        const nextTitle = e.target.value;
                        if (!updateData.descriptions?.title) {
                          setShowEnglishHint(true);
                        }
                        if (hasAttemptedSave && nextTitle.trim()) {
                          setHasAttemptedSave(false);
                        }
                        setUpdateData({
                          ...updateData,
                          descriptions: {
                            ...updateData.descriptions,
                            title: nextTitle,
                          },
                        });
                      }}
                      value={updateData.descriptions?.title}
                    />
                    <TextBoxWithTitle
                      nRows={6}
                      title={"Short description about your home"}
                      onChange={(e) =>
                        setUpdateData({
                          ...updateData,
                          descriptions: {
                            ...updateData.descriptions,
                            shortDescription: e.target.value,
                          },
                        })
                      }
                      value={updateData.descriptions?.shortDescription}
                    />
                    <TextBoxWithTitle
                      nRows={6}
                      title={"Short description about your neighbourhood"}
                      onChange={(e) =>
                        setUpdateData({
                          ...updateData,
                          descriptions: {
                            ...updateData.descriptions,
                            neighbourhood: e.target.value,
                          },
                        })
                      }
                      value={updateData.descriptions?.neighbourhood}
                    />
                    <TextBoxWithTitle
                      nRows={6}
                      title={"Preferred destinations for swap"}
                      onChange={(e) =>
                        setUpdateData({
                          ...updateData,
                          descriptions: {
                            ...updateData.descriptions,
                            prefferedDestinations: e.target.value,
                          },
                        })
                      }
                      value={updateData.descriptions?.prefferedDestinations}
                    />
                    <Typography variant="h6" component="p">
                      {"Availability"}
                    </Typography>
                    <AvailabilitySelector
                      availabilityData={updateData?.availability}
                      setAvailabilityData={(data) =>
                        setUpdateData({
                          ...updateData,
                          availability: data,
                        })
                      }
                    />
                  </Box>
                </InnerContentBox>
              </Grid>
              <Grid
                item
                xs={12}
                sm={4}
                sx={{ display: "flex", flexDirection: "column" }}
              >
                <InnerContentBox title={""} sx={boxStyle}>
                  <Grid container spacing={2} justifyContent="center">
                    <Grid item xs={12}>
                      <Typography
                        variant="h5"
                        component="h5"
                        sx={{ ...textStyle }}
                      >
                        Photos
                      </Typography>
                    </Grid>

                    {[...Array(4)].map((_, rowIndex) =>
                      [...Array(2)].map((_, colIndex) => (
                        <Grid
                          item
                          xs={12}
                          sm={6}
                          key={rowIndex * 2 + colIndex}
                        >
                          <AvatarDropzone
                            onDrop={onDrop}
                            imageUrl={
                              (home?.files ?? [])[rowIndex * 2 + colIndex]
                                ?.downloadUrl
                            }
                          />
                        </Grid>
                      )),
                    )}
                    {/* </Box> */}
                    <Grid item xs={12} container justifyContent="flex-end">
                      <SecondaryButton
                        text={"Edit and view"}
                        onClick={() => handleOpen(0)}
                      />
                    </Grid>
                  </Grid>
                </InnerContentBox>
              </Grid>
            </Grid>
          </Box>

          {!isOnboardingEmbed && (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                gap: 5,
                mt: 10,
                // pb: 15,
              }}
            >
              <MainButton
                isLoading={isUpdatingOrCreating}
                text={"Save"}
                onClick={() => {
                  void handleUpdate();
                }}
              />
              {home?._id ? (
                updateData?.isActive ? (
                  <Tooltip
                    title={
                      "Inactivating your home will make it unavailable for swaps. You can reactivate it anytime."
                    }
                    arrow
                    placement="top"
                  >
                    <span>
                      <SecondaryButton
                        isLoading={isUpdatingOrCreating}
                        text={"Inactivate"}
                        onClick={() => {
                          // send the intended isActive value immediately to handleUpdate
                          void handleUpdate({ isActive: false });
                        }}
                      />
                    </span>
                  </Tooltip>
                ) : (
                  <MainButton
                    isLoading={isUpdatingOrCreating}
                    text={"Activate"}
                    onClick={() => {
                      void handleUpdate({ isActive: true });
                    }}
                  />
                )
              ) : (
                <></>
              )}
            </Box>
          )}
          <ImagesModal
            files={home?.files!}
            open={open}
            currentImageIndex={currentImageIndex}
            homeId={String(home?._id)}
            onClose={handleClose}
            onPrev={handlePrev}
            onNext={handleNext}
          />
        </Box>
      </ThemeProvider>
      {!isOnboardingEmbed && <Footer />}
      <Snackbar
        open={showEnglishHint}
        autoHideDuration={3000}
        onClose={() => setShowEnglishHint(false)}
        message="Please write your home ad in English."
        ContentProps={{
          sx: {
            background: backgroundColor, // your dark blue
            color: "#fff", // text color
          },
        }}
      />
    </Box>
  );
}
